import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Copy, Users, MessageSquare, Newspaper, NotebookText, Trophy, CalendarDays, Crown, UserMinus, LogOut, Trash2, CalendarClock, UserPlus, Loader2 } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { GroupSessions } from "@/components/groups/GroupSessions";
import { api, formatApiError } from "@/lib/api";
import { GroupChat } from "@/components/groups/GroupChat";
import { GroupBoard } from "@/components/groups/GroupBoard";
import { GroupRevise } from "@/components/groups/GroupRevise";
import { GroupTimetable } from "@/components/groups/GroupTimetable";

const TABS = [
  ["chat", "Chat", MessageSquare], ["sessions", "Sessions", CalendarClock], ["posts", "Posts", Newspaper], ["notes", "Notes", NotebookText],
  ["revise", "Revise", Trophy], ["timetable", "Timetable", CalendarDays],
];

const Members = ({ g, open, setOpen, reload }) => {
  const nav = useNavigate();
  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.name} from the group? They won't be able to rejoin with the code.`)) return;
    try { await api.delete(`/groups/${g.group_id}/members/${m.user_id}`); toast.success(`${m.name} removed`); reload(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const leave = async () => { if (!window.confirm("Leave this group?")) return; await api.post(`/groups/${g.group_id}/leave`).catch(() => {}); nav("/groups"); };
  const del = async () => { if (!window.confirm("Delete this group for everyone? Messages, posts and notes will be gone.")) return; await api.delete(`/groups/${g.group_id}`).catch(() => {}); nav("/groups"); };
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" data-testid="group-members-sheet" className="bg-surface text-white border-l border-gold-400/30 w-[90vw] sm:max-w-md overflow-y-auto">
        <SheetHeader><SheetTitle className="font-display text-3xl text-white text-left">Members · {g.member_list.length}</SheetTitle></SheetHeader>
        <ul className="mt-4 space-y-2">
          {g.member_list.map((m) => (
            <li key={m.user_id} data-testid={`member-${m.user_id}`} className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-surface-2 px-3 py-2.5">
              <span className="grid place-items-center w-9 h-9 rounded-full bg-navy-200 text-sm font-bold text-gold-300">{m.name.slice(0, 1)}</span>
              <span className="flex-1 min-w-0 truncate font-semibold">{m.name}{m.user_id === g.me && <span className="text-slate-500 font-normal"> (you)</span>}</span>
              {m.is_owner && <span className="inline-flex items-center gap-1 rounded-full bg-gold-400 px-2 py-0.5 text-xs font-bold text-black"><Crown className="w-3 h-3" />Creator</span>}
              {g.is_owner && !m.is_owner && <button data-testid={`member-remove-${m.user_id}`} onClick={() => remove(m)} aria-label={`Remove ${m.name}`} className="grid place-items-center w-8 h-8 rounded-lg text-slate-500 hover:bg-rose-600 hover:text-white"><UserMinus className="w-4 h-4" /></button>}
            </li>
          ))}
        </ul>
        <div className="mt-6">
          {g.is_owner
            ? <button data-testid="group-delete" onClick={del} className="w-full inline-flex items-center justify-center gap-2 rounded-2xl border border-rose-600/60 px-4 py-3 text-sm font-semibold text-rose-600 hover:bg-rose-600 hover:text-white"><Trash2 className="w-4 h-4" />Delete group</button>
            : <button data-testid="group-leave" onClick={leave} className="w-full inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-300 px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-rose-600 hover:text-white"><LogOut className="w-4 h-4" />Leave group</button>}
        </div>
      </SheetContent>
    </Sheet>
  );
};

const InviteDialog = ({ g, open, setOpen }) => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/groups/${g.group_id}/invite`, { email: email.trim() });
      toast.success(data?.kind === "in_app" ? "Invite sent. They'll see it in Daybreyk" : "Invite emailed with the group code");
      setEmail("");
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="group-invite-dialog" className="bg-surface border border-gold-400/30 text-white rounded-3xl max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl text-white">Invite to {g.name}</DialogTitle>
          <DialogDescription className="text-slate-600">If they use Daybreyk they get an invite to accept. If not, we email them a link with the code <b className="font-mono text-gold-300">{g.code}</b>.</DialogDescription>
        </DialogHeader>
        <form onSubmit={send} className="flex gap-2">
          <input data-testid="group-invite-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="friend@email.com" className="flex-1 min-w-0 h-11 rounded-2xl border border-slate-200 bg-surface-deep/60 px-4 text-sm text-white placeholder:text-slate-400 outline-none focus:border-gold-400" />
          <button data-testid="group-invite-send" disabled={busy || !email.includes("@")} className="inline-flex items-center gap-2 h-11 rounded-2xl bg-gold-400 px-5 text-sm font-bold text-black hover:bg-gold-300 disabled:opacity-40">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}Invite</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default function GroupPage() {
  const { gid } = useParams();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "chat";
  const [g, setG] = useState(null);
  const [open, setOpen] = useState(false);
  const [inviting, setInviting] = useState(false);
  const load = () => api.get(`/groups/${gid}`).then((r) => setG(r.data ?? false)).catch(() => setG(false));
  useEffect(() => { load(); }, [gid]); // eslint-disable-line react-hooks/exhaustive-deps
  const copy = async () => {
    const msg = `Join my Daybreyk group "${g.name}" with code ${g.code} at ${window.location.origin}/groups`;
    if (navigator.share) { try { await navigator.share({ title: g.name, text: msg }); return; } catch { /* fall back */ } }
    await navigator.clipboard?.writeText(g.code).catch(() => {});
    toast.success(`Code ${g.code} copied`);
  };

  if (g === false) return <main className="mx-auto max-w-3xl px-4 py-12 text-slate-500">Group not found. <Link to="/groups" className="text-gold-300 font-semibold">Back to groups</Link></main>;
  if (!g) return <main className="mx-auto max-w-5xl px-4 py-12"><div className="h-40 rounded-3xl bg-surface animate-pulse" /></main>;
  return (
    <main data-testid="group-page" className="mx-auto max-w-5xl px-4 sm:px-6 py-6 sm:py-10 space-y-5">
      <Link to="/groups" data-testid="group-back" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-white"><ArrowLeft className="w-4 h-4" />All groups</Link>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <p className="eyebrow">Group</p>
          <h1 data-testid="group-name" className="mt-1 font-display text-4xl sm:text-5xl text-white leading-none break-words">{g.name}</h1>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="group-code-copy" onClick={copy} className="inline-flex items-center gap-2 h-11 rounded-full border border-gold-400/60 bg-gold-400/10 px-4 text-sm font-semibold text-gold-300 hover:bg-gold-400 hover:text-black transition-colors">
            <span data-testid="group-code" className="font-mono text-base tracking-[0.25em]">{g.code}</span><Copy className="w-4 h-4" />
          </button>
          <button data-testid="group-invite-button" onClick={() => setInviting(true)} className="inline-flex items-center gap-2 h-11 rounded-full bg-gold-400 px-4 text-sm font-bold text-black hover:bg-gold-300"><UserPlus className="w-4 h-4" /><span className="hidden sm:inline">Invite</span></button>
          <button data-testid="group-members-button" onClick={() => setOpen(true)} className="inline-flex items-center gap-2 h-11 rounded-full border border-slate-200 px-4 text-sm font-semibold text-slate-700 hover:text-white"><Users className="w-4 h-4" />{g.member_list.length}</button>
        </div>
      </div>
      <nav data-testid="group-tabs" className="no-scrollbar -mx-4 px-4 sm:mx-0 sm:px-0 flex gap-1.5 overflow-x-auto">
        {TABS.map(([k, l, I]) => (
          <button key={k} data-testid={`group-tab-${k}`} onClick={() => setParams({ tab: k })}
            className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${tab === k ? "bg-gold-400 text-black" : "border border-slate-200 text-slate-600 hover:text-white"}`}>
            <I className="w-4 h-4" />{l}
          </button>
        ))}
      </nav>
      {tab === "chat" && <GroupChat gid={gid} me={g.me} />}
      {tab === "sessions" && <GroupSessions gid={gid} me={g.me} isOwner={g.is_owner} />}
      {(tab === "posts" || tab === "notes") && <GroupBoard gid={gid} kind={tab} me={g.me} isOwner={g.is_owner} />}
      {tab === "revise" && <GroupRevise gid={gid} me={g.me} isOwner={g.is_owner} />}
      {tab === "timetable" && <GroupTimetable gid={gid} />}
      <Members g={g} open={open} setOpen={setOpen} reload={load} />
      <InviteDialog g={g} open={inviting} setOpen={setInviting} />
    </main>
  );
}
